import { useState } from 'react'
import { PackageOpen, ScanLine } from 'lucide-react'
import { useCreateProduct } from '@/hooks/useProducts'
import { readDinEntry } from '@/lib/isbtDin'
import type { CreatedProduct, ProductCatalogEntry } from '@/types/products'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ProductCatalogPicker } from '@/components/procedure/ProductCatalogPicker'
import { cn } from '@/lib/utils'

type DinMode = 'scan' | 'generate'

/**
 * Receive Product — per the spec, NOT a standalone transaction: it runs from
 * the collection form's action bar when bags arrive at the lab (EPIC/other
 * order). A bag from outside carries a DIN already and is scanned in; one
 * originating here gets a DIN the server mints. A DIN typed by hand must come
 * with the check character printed beside it, which catches a mistyped digit
 * (z8nrz7fxwh); a scan of the barcode needs none. Either way the product is recorded
 * against this submission (the audit trail) and then goes to the
 * storage-assignment prompt, where Temp Locations are offered for incoming
 * bags awaiting processing.
 */
export function ReceiveProductDialog({
  open,
  onClose,
  submissionId,
  formDefinitionId,
  procedureId,
  patientMrn,
  patientName,
  patientDob,
  requiresPatientHistory,
  onReceived,
}: {
  open: boolean
  onClose: () => void
  submissionId: string
  formDefinitionId: string
  procedureId: string
  patientMrn: string
  patientName: string | null
  patientDob: string | null
  requiresPatientHistory: boolean
  onReceived: (product: CreatedProduct, catalogEntry: ProductCatalogEntry) => void
}) {
  const createProduct = useCreateProduct()

  const [selected, setSelected] = useState<ProductCatalogEntry | null>(null)
  const [dinMode, setDinMode] = useState<DinMode>('scan')
  const [scannedDin, setScannedDin] = useState('')
  const [checkCharacter, setCheckCharacter] = useState('')
  const [volumeMl, setVolumeMl] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const entry = readDinEntry(scannedDin, checkCharacter)
  // A barcode scan carries no check character; only a typed DIN asks for one.
  const typed = !/^(\][A-Za-z][0-9A-Za-z])?=/.test(scannedDin.trim())
  const dinReady = dinMode === 'generate' || entry.ok

  function handleOpenChange(next: boolean) {
    if (!next) {
      setSelected(null)
      setDinMode('scan')
      setScannedDin('')
      setCheckCharacter('')
      setVolumeMl('')
      setSaveError(null)
      onClose()
    }
  }

  async function handleReceive() {
    if (!selected || !dinReady) return
    setSaving(true)
    setSaveError(null)
    try {
      // A scanned DIN is the bag's identity — a conflict there is a real error
      // to show, never something to silently renumber. Generated: no DIN, the
      // server mints it.
      const supplied =
        dinMode === 'scan' && entry.ok
          ? {
              din: entry.din,
              dinFlag: entry.flag ?? undefined,
              dinCheckCharacter: entry.checkCharacter ?? undefined,
            }
          : {}
      const product = await createProduct.mutateAsync({
        catalogEntryId: selected.id,
        ...supplied,
        submissionId,
        formDefinitionId,
        procedureId,
        patientMrn: requiresPatientHistory ? patientMrn : null,
        patientName: requiresPatientHistory ? patientName : null,
        patientDob: requiresPatientHistory ? patientDob : null,
        volumeMl: volumeMl.trim() ? Number(volumeMl) : undefined,
      })
      const catalogEntry = selected
      handleOpenChange(false)
      onReceived(product, catalogEntry)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to receive product.'
      setSaveError(message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-5xl'>
        <DialogHeader>
          <DialogTitle>Receive product</DialogTitle>
          <DialogDescription>
            Register an arriving product against this form. Scan the DIN printed on the bag, or
            generate one when the product originates here.
          </DialogDescription>
        </DialogHeader>

        <div className='flex flex-wrap items-center gap-2'>
          <div className='flex overflow-hidden rounded-lg border'>
            {(
              [
                { mode: 'scan', label: 'Scan / enter DIN', icon: ScanLine },
                { mode: 'generate', label: 'Generate DIN', icon: PackageOpen },
              ] as const
            ).map(({ mode, label, icon: Icon }) => (
              <button
                key={mode}
                type='button'
                onClick={() => setDinMode(mode)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors',
                  dinMode === mode
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background hover:bg-accent',
                )}
              >
                <Icon className='h-3.5 w-3.5' />
                {label}
              </button>
            ))}
          </div>
          {dinMode === 'scan' ? (
            <Input
              value={scannedDin}
              onChange={e => setScannedDin(e.target.value)}
              // Barcode scanners type-and-Enter into a focused input, so this
              // must stay a plain text input and never submit-on-Enter.
              onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
              placeholder='Scan or type the DIN on the bag'
              aria-label='DIN'
              className='w-72 font-mono'
              autoFocus
            />
          ) : (
            <p className='text-sm text-muted-foreground'>
              A DIN will be generated when the product is received.
            </p>
          )}
          {dinMode === 'scan' && typed && (
            <Input
              value={checkCharacter}
              onChange={e => setCheckCharacter(e.target.value.slice(-1))}
              onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
              placeholder='Check'
              aria-label='Check character'
              title='The character printed in the box after the DIN'
              className='w-20 text-center font-mono uppercase'
            />
          )}
        </div>
        {/* Not while the DIN is still being typed: only once it is all there. */}
        {dinMode === 'scan' && !entry.ok && (scannedDin.trim().length >= 13 || checkCharacter) && (
          <p className='text-sm text-destructive' role='alert'>
            {entry.error}
          </p>
        )}

        <ProductCatalogPicker selected={selected} onSelect={setSelected} />

        {saveError && <p className='text-sm text-destructive'>{saveError}</p>}

        {/* An arriving bag has a volume like any other product, and the same
            three actions later depend on it. */}
        <div className='flex items-end justify-between gap-4 border-t pt-3'>
          <div className='space-y-1'>
            <label className='text-sm font-medium' htmlFor='receive-product-volume'>
              Volume (mL)
            </label>
            <Input
              id='receive-product-volume'
              type='number'
              min={0}
              value={volumeMl}
              onChange={e => setVolumeMl(e.target.value)}
              placeholder='mL'
              className='max-w-[10rem]'
            />
          </div>
          <div className='flex gap-2'>
            <Button variant='outline' onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleReceive} disabled={!selected || !dinReady || saving}>
              {saving ? 'Receiving…' : 'Receive Product'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
